import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { putAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { formatDateLocale } from '@/helpers/dateHelper';
import {
  IsraeliIdChangeRequestDecisionDtoIn,
  SupportedIsraeliIdChangeRequestDtoIn,
} from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import { SupportedIsraeliIdChangeImpactDtoIn } from '@/models/in/SupportedIsraeliIdChangeImpactDtoIn';
import IsraeliIdChangeImpact from '../IsraeliIdChangeImpact/IsraeliIdChangeImpact';
import { israeliIdChangeBlocker } from '../IsraeliIdChangeImpact/israeliIdChangeBlocker';
import IsraeliIdChangeBlockerMessage from '../IsraeliIdChangeImpact/IsraeliIdChangeBlockerMessage';
import styles from './IsraeliIdChangeRequest.module.css';

interface ReviewIsraeliIdChangeRequestDialogProps {
  request: SupportedIsraeliIdChangeRequestDtoIn;
  onClose: () => void;
  /**
   * Called once the request was approved or rejected, with whether the mail to the coordinator went
   * out - null when the office chose not to send one.
   */
  onDecided: (decision: 'approved' | 'rejected', emailSent: boolean | null) => void;
}

// The server's reason when it refuses, not axios's "Request failed with status code 409"
const serverMessageOf = (err: unknown) =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' ? err.response.data.trim() : '';

/**
 * The office deciding on a coordinator's request to correct a supported's Israeli ID (HAT-1428).
 * Druk: before approving, the office sees everywhere the supported is and what the change affects -
 * the same summary as the direct edit. Approving goes through every check of that edit; a refusal
 * leaves the request pending, to be rejected with a reason the coordinator reads.
 */
const ReviewIsraeliIdChangeRequestDialog = ({ request, onClose, onDecided }: ReviewIsraeliIdChangeRequestDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [isRejecting, setIsRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Under the reason box, where it is typed - not at the bottom with the server's errors
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // On by default: the coordinator who asked should hear what was decided without coming to look
  const [notifyCoordinator, setNotifyCoordinator] = useState(true);

  const { data: impact, isLoading, isError } = useGetAuthApi<SupportedIsraeliIdChangeImpactDtoIn>(
    `management/supporteds/${request.contactId}/israeli-id-change-impact?newIsraeliId=${request.requestedIsraeliId}`
  );

  useEffect(() => {
    setIsRejecting(false);
    setReason('');
    setError(null);
    setReasonError(null);
  }, [request.id]);

  // What the approval would refuse - a coordinator's own card, or a number someone already holds.
  // Said instead of the summary, before the office clicks, with only «דחייה» left to offer.
  const blocker = impact ? israeliIdChangeBlocker(impact) : null;
  const supportedName = request.supportedName ?? '';

  // A taken number comes with the reason the coordinator will read, ready to send. It does not say
  // whose number it is - the coordinator is never told that.
  const startRejecting = () => {
    if (!reason.trim() && blocker?.kind === 'taken') {
      setReason(t('israeliIdChangeRequests.reasonTaken'));
    }
    setIsRejecting(true);
  };

  const decide = async (decision: 'approved' | 'rejected') => {
    if (decision === 'rejected' && !reason.trim()) {
      setReasonError(t('israeliIdChangeRequests.reasonRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const result = await putAuth<IsraeliIdChangeRequestDecisionDtoIn>(
        `management/israeli-id-change-requests/${request.id}/status/${decision}`,
        { Authorization: `Bearer ${currentUser?.token}` },
        {},
        decision === 'rejected' ? { reason: reason.trim(), notifyCoordinator } : { notifyCoordinator }
      );
      onDecided(decision, result?.emailSent ?? null);
    } catch (err) {
      setError(serverMessageOf(err) || t('israeliIdChangeRequests.decisionError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('israeliIdChangeRequests.reviewTitle', { name: request.supportedName ?? '' })}
    >
      <div className={styles.dialogContent}>
        <p className={styles.requestedBy}>
          {t('israeliIdChangeRequests.requestedBy', {
            name: request.requestedByName || request.requestedByEmail || '-',
            fund: request.fundName ? `${request.fundName} (${request.fundId})` : request.fundId,
            date: formatDateLocale(request.requestedAt),
          })}
        </p>
        {request.note && (
          <p className={styles.note}>
            <span className={styles.noteLabel}>{t('israeliIdChangeRequests.coordinatorNote')}</span> {request.note}
          </p>
        )}

        {isLoading && <p className={styles.muted}>{t('loading')}</p>}
        {isError && <p className={styles.formError}>{t('supportedsManagement.editSupportedDialog.errorLoadingImpact')}</p>}
        {impact && !blocker && (
          <IsraeliIdChangeImpact
            impact={impact}
            contactId={request.contactId}
            supportedName={supportedName}
            showPendingRequest={false}
            currentIsraeliId={request.currentIsraeliId ?? ''}
            newIsraeliId={request.requestedIsraeliId}
          />
        )}
        {blocker && (
          <IsraeliIdChangeBlockerMessage
            blocker={blocker}
            contactId={request.contactId}
            supportedName={supportedName}
            newIsraeliId={request.requestedIsraeliId}
          />
        )}

        {isRejecting && (
          <div className={styles.reasonSection}>
            <label htmlFor="rejectionReason" className={styles.reasonLabel}>
              {t('israeliIdChangeRequests.rejectionReason')}
            </label>
            <textarea
              id="rejectionReason"
              rows={3}
              maxLength={500}
              className={styles.reasonInput}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (reasonError) setReasonError(null);
              }}
            />
            {reasonError && <p className={styles.formError} role="alert">{reasonError}</p>}
          </div>
        )}

        {error && <p className={styles.formError} role="alert">{error}</p>}

        {request.requestedByEmail && (
          <label className={styles.notify}>
            <input
              type="checkbox"
              checked={notifyCoordinator}
              onChange={(e) => setNotifyCoordinator(e.target.checked)}
            />
            <span>
              {t('israeliIdChangeRequests.notifyCoordinator', { name: request.requestedByName || request.requestedByEmail })}
            </span>
          </label>
        )}

        <div className={styles.dialogActions}>
          {isRejecting ? (
            <>
              <Button variant="white" className={styles.secondaryButton} onClick={() => setIsRejecting(false)} disabled={isSubmitting}>
                {t('israeliIdChangeRequests.back')}
              </Button>
              <Button variant="primary" className={styles.actionButton} onClick={() => decide('rejected')} disabled={isSubmitting}>
                {isSubmitting ? t('saving') : t('israeliIdChangeRequests.sendRejection')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="white" className={styles.secondaryButton} onClick={startRejecting} disabled={isSubmitting}>
                {t('israeliIdChangeRequests.reject')}
              </Button>
              {/* The server refuses what the blocker names, so no button to try */}
              {!blocker && (
                <Button
                  variant="primary"
                  className={styles.actionButton}
                  onClick={() => decide('approved')}
                  disabled={isSubmitting || !impact}
                >
                  {isSubmitting ? t('saving') : t('israeliIdChangeRequests.approve')}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default ReviewIsraeliIdChangeRequestDialog;
